import {useCallback} from 'react';
import {Row} from 'react-table';

import {Action} from '@ELib/utilities/types';

import {
  ETableAction,
  ETableMultiAction,
  ETableRow,
  ETableRowActionHandler,
  ETableRowExpansion,
  ETableSingleAction,
} from './types';

// code moved from webapp, disable conflicting rules in webapp-lib for now.
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-argument */

export const useActionsHandlerForSubRow = (
  tableRowExpansion?: ETableRowExpansion
): ETableRowActionHandler =>
  useCallback(
    (row?: Row<ETableRow>) => {
      if (
        !row ||
        !tableRowExpansion ||
        !Array.isArray(tableRowExpansion) ||
        (Array.isArray(tableRowExpansion) && tableRowExpansion.length === 0)
      ) {
        // The table row expansion is not a list of subrows (ETableSubRow<Key>[]),
        // but a different element (for example a complete table that should be displayed when expanded).
        return [];
      }

      if (hasNoActions(row)) {
        return [];
      }

      return mapActions([row]);
    },
    [tableRowExpansion]
  );

// Simplifies the logic used to create the callback function which in turn returns the allowed actions on a row.
// The returned actions might be called with a row (case via context menu) or without (case keyboard shortcut)

export const useActionsHandler = (
  rows: Row<ETableRow>[],
  selectedItemIds: Set<string>
): ETableRowActionHandler => {
  const selectedItemsIdsAsString = [...selectedItemIds].join(',');
  return useCallback(
    (row?: Row<ETableRow>) => {
      if (row) {
        if (hasNoActions(row)) {
          return [];
        }

        const isRowPartOfSelection = selectedItemIds.has(row.original.id);
        if (!isRowPartOfSelection) {
          return mapActions([row]);
        }
      }

      const selectedRows = rows.filter(({original}) =>
        selectedItemIds.has(original.id)
      );

      if (selectedRows.length > 0) {
        return mapActions(selectedRows);
      } else {
        return [];
      }
    },
    // line disabled because selectedItemIds is stringed together to get better control over refresh
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, selectedItemsIdsAsString]
  );
};

export const isActionActiveForAllRows = (
  selectedRows: Row<ETableRow>[],
  action: ETableAction
): boolean => {
  if (selectedRows.length === 0 || !selectedRows[0]) {
    return false;
  }

  if (selectedRows[0].original.options?.multiActions?.length) {
    return selectedRows.every((selectedRow) =>
      selectedRow.original.options?.multiActions?.some(
        (act) => act.id === action.id && !act.inactive
      )
    );
  }

  return (
    selectedRows.length === 1 &&
    Boolean(
      selectedRows[0].original.options?.singleActions?.some(
        (act) => act.id === action.id && !act.inactive
      )
    )
  );
};

const wrapAction = async (
  action: ETableMultiAction | ETableSingleAction,
  originals: any | any[]
) => {
  if (action.callback) {
    await action.callback(originals);
  }
};

const hasNoActions = (row: Row<ETableRow>) => {
  const {singleActions, multiActions} = row.original.options ?? {};
  return !singleActions?.length && !multiActions?.length;
};

const mapActions = (rows: Row<ETableRow>[]): ETableAction[] => {
  if (rows.length === 0 || !rows[0] || !rows[0].original.options) {
    return [];
  }
  const [firstRow] = rows;
  const {singleActions, multiActions} = rows[0].original.options;
  const singleRowSelection = rows.length === 1;
  if (singleRowSelection && singleActions) {
    return singleActions.map((singleAction) => ({
      ...singleAction,
      text: getText(singleAction, firstRow.original.raw),
      inactive: getIsInactive(singleAction, firstRow.original.raw),
      callback: () => wrapAction(singleAction, firstRow.original.raw),
      subActions: singleAction.subActions?.map((subAction) => ({
        ...subAction,
        callback: () => wrapAction(subAction, firstRow.original.raw),
      })),
    }));
  }

  // applies also for single row selections with no singleActions
  const rowOriginals = rows.map(({original: {raw}}) => raw);
  return (
    multiActions?.map((multiAction) => ({
      ...multiAction,
      text: getText(multiAction, rowOriginals),
      callback: () => wrapAction(multiAction, rowOriginals),
      subActions: multiAction.subActions?.map((subAction) => ({
        ...subAction,
        callback: () => wrapAction(subAction, rowOriginals),
      })),
      inactive: !getIsInactiveForAllRows(rows, multiAction, rowOriginals),
    })) ?? []
  );
};

function getIsInactiveForAllRows<T extends object>(
  selectedRows: Row<ETableRow>[],
  action: Action<T>,
  selectedItems: T
): boolean {
  if (selectedRows.length === 0 || !selectedRows[0]) {
    return false;
  }

  const [firstRow] = selectedRows;
  if (firstRow.original.options?.multiActions?.length) {
    return selectedRows.every((selectedRow) =>
      selectedRow.original.options?.multiActions?.some(
        (act) => act.id === action.id && !getIsInactive(action, selectedItems)
      )
    );
  }

  return (
    selectedRows.length === 1 &&
    Boolean(
      firstRow.original.options?.singleActions?.some(
        (act) =>
          act.id === action.id && !getIsInactive(action, firstRow.original.raw)
      )
    )
  );
}

function getIsInactive<T extends object>(
  action: Action<T>,
  selectedRows: T
): boolean {
  return typeof action.inactive === 'function'
    ? action.inactive(selectedRows)
    : action.inactive ?? false;
}

function getText<T extends object>(action: Action<T>, selectedRows: T) {
  return typeof action.text === 'function'
    ? action.text(selectedRows)
    : action.text;
}
